import { DashboardPlatformAvailable } from "@/api/__generated__/schemas";
import { isLanguage, Language } from "@/i18n/i18n.types";
import { AccessibleOrganization } from "@/types/accessibleFederation.types";
import { AccessiblePlatform } from "@/types/accessiblePlatform.types";
import {
  AvailablePlatform,
  AvailablePlatformOwner,
  AvailableUserRef,
} from "@/types/availablePlatform.types";
import { CountryCode, CurrencyCode } from "@/types/common.types";
import { SportRef } from "@/types/sport.types";

import { mapToCapabilitySet } from "./capability.mappers";
import { mapToTimeZone } from "./common.mappers";
import { mapToTerminologySettings } from "./platform.mappers";

/**
 * A platform Extras switch on the `available` projection, tolerated as absent
 * until the generated dashboard client carries it. A non-boolean read (absent /
 * null) falls back to `whenAbsent`, which defaults to off — the same meaning the
 * backend's nullable column has. `enableUserCreation` passes `true` instead: it
 * *disables* an affordance, so a deploy gap must never hide the create-user
 * surfaces.
 */
const readAvailablePlatformExtraFlag = (
  source: object,
  key: "enableLTS10" | "enableUspaLicenses" | "enableUserCreation",
  whenAbsent = false
): boolean => {
  const value = (source as Record<string, unknown>)[key];
  return typeof value === "boolean" ? value : whenAbsent;
};

/**
 * REST `DashboardPlatformAvailable` → FE `AvailablePlatform`.
 *
 * Both the warm path (`GET /platforms/available/:id`) and the cold-start fallback
 * (`GET /platforms/me/available`) return this same shape — there's no GraphQL
 * branch anymore. BE returns `countries[]`, `currencies[]`, `languages[]` as
 * parsed arrays of canonical codes (ISO 3166-1 / ISO 4217 / BCP-47); no
 * `JSON.parse`, no legacy display-name translation table.
 */
export const mapToAvailablePlatform = (input: DashboardPlatformAvailable): AvailablePlatform => ({
  id: input.id,
  title: input.title ?? "",
  logo: input.lightLogo ?? input.darkLogo ?? null,
  countries: (input.countries ?? []).filter((code): code is CountryCode => Boolean(code)),
  currencies: (input.currencies ?? []).filter((code): code is CurrencyCode => Boolean(code)),
  languages: (input.languages ?? []).filter((tag): tag is Language => isLanguage(tag)),
  timeZone: input.timeZone ? mapToTimeZone(input.timeZone) : null,
  primaryColor: input.primaryColor ?? null,
  lightLogo: input.lightLogo ?? null,
  darkLogo: input.darkLogo ?? null,
  lightEmblem: input.lightEmblem ?? null,
  darkEmblem: input.darkEmblem ?? null,
  favicon: input.favicon ?? null,
  platformLink: input.platformLink ?? null,
  enableToursLeagues: input.enableToursLeagues ?? false,
  enableRankings: input.enableRankings ?? false,
  // Extras switches — read defensively until `yarn rest:codegen` picks them up
  // from the updated platform contract. The two category flags read as off when
  // absent; user creation reads as allowed.
  enableLTS10: readAvailablePlatformExtraFlag(input, "enableLTS10"),
  enableUspaLicenses: readAvailablePlatformExtraFlag(input, "enableUspaLicenses"),
  enableUserCreation: readAvailablePlatformExtraFlag(input, "enableUserCreation", true),
  sports: (input.sports ?? []).map(mapToSportRefFromAvailable),
  owner: input.owner ? mapToAvailablePlatformOwner(input.owner) : null,
  dataOwner: input.dataOwner ? mapToAvailablePlatformOwner(input.dataOwner) : null,
  terminology: mapToTerminologySettings(input.terminology),
  // `available` audience does not expose fee scalars. Surfaces that need them
  // (e.g. `CreateLicensePage`) should fetch via the `accessible` audience.
  tournatedLicenseFee: 0,
  partnerLicenseFee: 0,
  // Defensive: `mapToCapabilitySet` tolerates a BE that has not shipped the
  // field yet (deploy gap) by resolving to an empty set — under strict RBAC an
  // empty set denies access (fail closed) until the field ships.
  capabilities: mapToCapabilitySet(input.capabilities),
});

const mapToSportRefFromAvailable = (input: {
  id: number;
  title?: string | null;
  icon?: string | null;
}): SportRef => ({
  id: input.id,
  title: input.title ?? null,
  icon: input.icon ?? null,
});

const mapToAvailablePlatformOwner = (
  input: NonNullable<DashboardPlatformAvailable["owner"]>
): AvailablePlatformOwner => ({
  id: input.id,
  mainManager: input.mainManager ? mapToAvailableUserRef(input.mainManager) : null,
  // Defensive: `mapToCapabilitySet` resolves a missing field (deploy gap) to an
  // empty set. For `dataOwner` these feed the platform-only active scope union.
  capabilities: mapToCapabilitySet(input.capabilities),
});

/**
 * Project the slim current `AvailablePlatform` (from `CurrentPlatformContext`) into the
 * `AccessiblePlatform` shape the org fees form consumes — WITHOUT refetching the heavy
 * accessible-platform payload.
 *
 * Only the fields the fees pipeline actually reads are populated faithfully:
 * - `id`, `currencies` — drive the form's default fee schedule + currency picker.
 * - `owner` (slim `{id, mainManager}`) — drives `canEditPartnerFees` permission gating.
 * - `fees` — left empty; the federation's existing custom fees ride on the federation
 *   detail's `customFees` (mapped separately via `mapFederationCustomFeesToOrganizationFees`),
 *   and platform/default-mode schedules are computed from EUR constants, not this payload.
 * Everything else is defaulted; surfaces needing accurate values should fetch the
 * `accessible` audience directly.
 */
export const mapAvailablePlatformToAccessiblePlatform = (
  platform: AvailablePlatform
): AccessiblePlatform => ({
  id: platform.id,
  title: platform.title,
  logo: platform.logo,
  platformType: null,
  status: null,
  countries: platform.countries,
  currencies: platform.currencies,
  languages: platform.languages,
  timeZone: platform.timeZone,
  primaryColor: platform.primaryColor,
  lightLogo: platform.lightLogo,
  darkLogo: platform.darkLogo,
  lightEmblem: platform.lightEmblem,
  darkEmblem: platform.darkEmblem,
  favicon: platform.favicon,
  platformLink: platform.platformLink,
  tournatedEntryFee: 0,
  tournatedLicenseFee: platform.tournatedLicenseFee,
  partnerEntryFee: 0,
  partnerLicenseFee: platform.partnerLicenseFee,
  fees: { entry: [], license: [] },
  owner: platform.owner ? mapAvailablePlatformOwnerToAccessibleOrganization(platform.owner) : null,
});

/**
 * Minimal slim `AvailablePlatformOwner` → `AccessibleOrganization`. Only `id` and the
 * slim `mainManager` are real (used by `canEditPartnerFees` / `isFederationOwner`); the
 * remaining fields are defaulted because the `available` audience does not surface them.
 */
const mapAvailablePlatformOwnerToAccessibleOrganization = (
  owner: AvailablePlatformOwner
): AccessibleOrganization => ({
  id: owner.id,
  name: "",
  abbreviation: null,
  logo: null,
  organizationTypes: [],
  countries: [],
  geographicScope: null,
  about: null,
  contactEmail: null,
  contactPhone: null,
  website: null,
  status: "active",
  enableLicenses: false,
  enableCertificates: false,
  enableApplicationForms: false,
  enableRankings: false,
  mainManager: owner.mainManager,
});

export const mapToAvailableUserRef = (input: {
  id: string;
  name?: string | null;
  surname?: string | null;
  avatarFile?: { url: string | null; fileId: string } | null;
  legacyAvatarUrl?: string | null;
}): AvailableUserRef => ({
  id: input.id,
  name: input.name ?? null,
  surname: input.surname ?? null,
  avatarFile: input.avatarFile
    ? { url: input.avatarFile.url ?? null, fileId: input.avatarFile.fileId ?? null }
    : null,
  legacyFileUrl: input.legacyAvatarUrl ?? null,
});
